import type { MsaColorScheme } from "@components/msa-viewer/types";
import type { CollectionOperation } from "@db/operationLog";
import type { BlastResult, BlastSearch } from "@fetch/blast/types";
import type { InterproResult, InterproSearch } from "@fetch/interpro/types";
import type {
  SequenceCheckpoint,
  SequenceOperation,
} from "@history/operations";
import type { Frame } from "@lib/genetics";
import Dexie, { type EntityTable } from "dexie";
import type {
  GnomuxAlignedSequence,
  GnomuxAlignment,
  GnomuxChromatogram,
  GnomuxFeature,
  GnomuxPrimer,
  GnomuxSequence,
  GnomuxTrackConfig,
  GnomuxTreeContent,
  SequenceChunk,
} from "./content";
import type { FeatureUsageRecord } from "./featureUsage";
import type { ImportRecord } from "./imports";
import {
  defaultPrimerParams,
  defaultViewerDefaults,
  registerMigrations,
} from "./migrations";
import type { GnomuxDirectory, GnomuxFile } from "./objects";

export type PrimerDesignParams = {
  productSizeMin: number;
  productSizeMax: number;
  numReturn: number;
  primerMinSize: number;
  primerOptSize: number;
  primerMaxSize: number;
  primerMinTm: number;
  primerOptTm: number;
  primerMaxTm: number;
  primerMinGc: number;
  primerMaxGc: number;
  primerSaltConc: number;
  primerDnaConc: number;
  maxDegeneracy: number;
};

export type ComplementMode = "none" | "dna" | "rna";

export type SequenceViewerDefaults = {
  showFeatures: boolean;
  showRestrictionSites: boolean;
  showGcContent: boolean;
  showSequenceGaps: boolean;
  complementMode: ComplementMode;
  translationFrames: Frame[];
  geneticCodeId?: number;
};

export type MsaViewerDefaults = {
  visibleTracks: string[];
  colorScheme?: MsaColorScheme;
  trackOrder?: string[];
};

export type CollectionViewerDefaults = {
  showGcContent: boolean;
};

export type ViewerDefaults = {
  sequence: SequenceViewerDefaults;
  msa: MsaViewerDefaults;
  collection: CollectionViewerDefaults;
};

/** The result of validating NCBI credentials against the Entrez API. */
export type NcbiValidationStatus =
  | "success"
  | "invalid_credentials"
  | "network_error"
  | "api_error";

/**
 * Persisted state from the last NCBI credential validation run.
 *
 * Stored on the settings record so the Services page can show the most recent
 * result without re-validating. Absent when credentials have never been
 * validated.
 */
export type NcbiValidationState = {
  status: NcbiValidationStatus;
  /** When the validation was last run. */
  timestamp: Date;
  /** A human-readable error description. Present only for non-success statuses. */
  message?: string;
};

export type AutoEmptyTrashDays = 0 | 7 | 14 | 30 | 60 | 90;

export type Settings = {
  id: "settings";
  ncbiUsername: string;
  ncbiApiKey: string;
  ebiEmail: string;
  storageLimit: number;
  primerParams: PrimerDesignParams;
  viewerDefaults: ViewerDefaults;
  promptToRenameNewFiles: boolean;
  autoEmptyTrashDays: AutoEmptyTrashDays;
  completionTracking: boolean;
  ncbiValidation?: NcbiValidationState;
};

export type RecentView = {
  objectId: string;
  viewedAt: Date;
  ownerId: string;
};

export type CachedUser = {
  id: "cachedUser";
  userId: string;
  email: string;
  name: string;
  cachedAt: Date;
};

export const db = new Dexie("gnomux") as Dexie & {
  objects: EntityTable<GnomuxFile | GnomuxDirectory, "id">;
  alignments: EntityTable<GnomuxAlignment, "id">;
  alignedSequences: EntityTable<GnomuxAlignedSequence, "id">;
  sequences: EntityTable<GnomuxSequence, "id">;
  sequenceChunks: EntityTable<SequenceChunk, "sequenceId">;
  chromatograms: EntityTable<GnomuxChromatogram, "id">;
  features: EntityTable<GnomuxFeature, "id">;
  primers: EntityTable<GnomuxPrimer, "id">;
  settings: EntityTable<Settings, "id">;
  trackConfigs: EntityTable<GnomuxTrackConfig, "id">;
  recents: EntityTable<RecentView, "objectId">;
  sequenceOperations: EntityTable<SequenceOperation, "id">;
  sequenceCheckpoints: EntityTable<SequenceCheckpoint, "id">;
  collectionOperations: EntityTable<CollectionOperation, "id">;
  cachedUser: EntityTable<CachedUser, "id">;
  blastSearches: EntityTable<BlastSearch, "id">;
  blastResults: EntityTable<BlastResult, "id">;
  interproSearches: EntityTable<InterproSearch, "id">;
  interproResults: EntityTable<InterproResult, "id">;
  treeContent: EntityTable<GnomuxTreeContent, "id">;
  imports: EntityTable<ImportRecord, "id">;
  featureUsage: EntityTable<FeatureUsageRecord, "featureId">;
};

registerMigrations(db);

export { defaultPrimerParams, defaultViewerDefaults } from "./migrations";

export const SEQUENCE_TABLES = [
  db.sequences,
  db.sequenceChunks,
  db.sequenceCheckpoints,
  db.features,
  db.primers,
  db.objects,
] as const;

const defaultSettings: Settings = {
  id: "settings",
  ncbiUsername: "",
  ncbiApiKey: "",
  ebiEmail: "",
  storageLimit: 1024 * 1024 * 1024,
  primerParams: defaultPrimerParams,
  viewerDefaults: defaultViewerDefaults,
  promptToRenameNewFiles: true,
  autoEmptyTrashDays: 30,
  completionTracking: true,
};

db.on("populate", () => {
  db.settings.add(defaultSettings);
});

db.on("ready", async () => {
  const settings = await db.settings.get("settings");
  if (!settings) {
    await db.settings.add(defaultSettings);
  }
});

if (import.meta.env.DEV && import.meta.env.MODE !== "test") {
  db.on("ready", () => {
    import("./seed").then(({ seedExampleData }) => seedExampleData());
  });
}
